import { channelsInstance, instance } from '@/api/instances'
import { addMixedDataLog } from '@/components/pillars/utils/addLog'
import { DEMAND_STATUS } from '@/models/demands'
import { getNewRange } from '@/utils/pagination'

const PAGINATION_LIMIT = 10

const addNewDemand = (_, { personId, payload }) => {
  return instance.post(`persons/${personId}/health_demand`, {
    json: payload
  })
}

const updateDemand = (_, { personId, payload }) => {
  return instance.put(`persons/${personId}/health_demand/${payload.caseId}`, {
    json: payload
  })
}

const deleteDemand = (_, { personId, demandId }) => {
  return instance.delete(`persons/${personId}/health_demand/${demandId}`)
}

const fetchDemands = async (store, { personId, type = null, signal }) => {
  store.commit('errors/setCriticalError', null, { root: true })
  const personID = personId || store.rootState.pillars.personId
  try {
    const filterByType = type ? `?filter={type=${type}}` : ''
    const getDemands = `persons/${personID}/health_demand/filters${filterByType}`
    const demandsList = await instance.get(getDemands, { signal })

    if (store.rootState.pillars.personId === personID) {
      store.commit('setDemands', demandsList)
      return demandsList
    }
    addMixedDataLog('fetchDemands')
    store.commit(
      'errors/setCriticalError',
      { type: 'mixed_data', context: 'demands' },
      { root: true }
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/fetchDemands' },
      { root: true }
    )
  }
}

const fetchDemandsWithBaseRiskRating = async (store, { personId, signal }) => {
  store.commit('errors/setCriticalError', null, { root: true })
  const personID = personId || store.rootState.pillars.personId

  try {
    const getDemands = `persons/${personID}/health_demand/risk_rating`
    const demandsList = await instance.get(getDemands, { signal })

    if (store.rootState.pillars.personId === personID) {
      return demandsList
    }
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/fetchDemandsWithBaseRiskRating' },
      { root: true }
    )
  }
}

const fetchDemandsPaginated = async (
  store,
  { personId, type = null, range, signal }
) => {
  store.commit('errors/setCriticalError', null, { root: true })

  const filtersStore = store.state.pagination.filters

  const filters = {
    ...filtersStore,
    ...{ type: type && type.type !== null && type },
    staffsIds: filtersStore?.staffsIds
      ? filtersStore.staffsIds.map(it => it.id)
      : null
  }

  try {
    const getDemands = `persons/${personId}/health_demand`
    const demandsList = await instance.get(getDemands, {
      signal,
      params: {
        range: JSON.stringify(range),
        filter: JSON.stringify(filters)
      }
    })

    if (store.rootState.pillars.personId !== personId) {
      addMixedDataLog('fetchDemandsPaginated')
      store.commit(
        'errors/setCriticalError',
        { type: 'mixed_data', context: 'demands' },
        { root: true }
      )

      return []
    }

    return [false, demandsList]
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        context: 'demands',
        request: 'pillars/demands/fetchDemandsPaginated'
      },
      { root: true }
    )
  }

  return [true, null]
}

const fetchCurrentDemandDetails = async (store, { personId, demandId }) => {
  try {
    const currentDemandDetails = await instance.get(
      `persons/${personId}/health_demand/${demandId}/current`
    )

    store.commit('setCurrentDemandDetails', currentDemandDetails)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/fetchCurrentDemandDetails' },
      { root: true }
    )
  }
}

const fetchCurrentDemandRecords = async (store, { personId, demandId }) => {
  try {
    const range = getNewRange({
      currRange: store.state.pagination.range,
      PAGINATION_LIMIT
    })

    const currentDemandRecords = await instance.get(
      `persons/${personId}/health_demand/${demandId}/records`,
      {
        params: {
          range: JSON.stringify(range)
        }
      }
    )

    store.commit('setCurrentPagination', { range })
    store.commit('addCurrentDemandRecords', currentDemandRecords)

    if (currentDemandRecords.length < PAGINATION_LIMIT) {
      store.commit('setCurrentPagination', { stopLoad: true })
    }
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/fetchCurrentDemandRecords' },
      { root: true }
    )
  }
}

const fetchActionRecommendations = async (store, { personId, demandIds }) => {
  try {
    const filterByDemandIds = demandIds
      ? `?filter={"case_ids":[${demandIds}]}`
      : ''
    const getActionRecommendations = `persons/${personId}/health_logics_action_recommendation/filters${filterByDemandIds}`
    const actionRecommendationsList = await instance.get(
      getActionRecommendations
    )

    store.commit('setActionRecommendations', actionRecommendationsList)

    return actionRecommendationsList
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/demands/fetchActionRecommendations',
        message:
          'Não foi possível buscar as recomendações da Wanda para a Lista de Demandas'
      },
      { root: true }
    )

    return []
  }
}

const validateDemand = async (store, { caseId, personId }) => {
  try {
    const currentDemand = store.state.currentDemandDetails
    if (
      !currentDemand ||
      currentDemand.caseId !== caseId ||
      currentDemand.status !== DEMAND_STATUS.PENDING
    ) {
      return
    }
    const splittedCurrentDemandStart = currentDemand.startedAt.split(' ')
    const date = splittedCurrentDemandStart[0].split('/').reverse().join('-')
    const hour = splittedCurrentDemandStart[1]
    const parsedStartedAt = new Date(`${date} ${hour}`)
      .toISOString()
      .split('Z')[0]
    const data = await instance.put(
      `persons/${personId}/health_demand/${caseId}`,
      {
        json: {
          responsibleStaffId: currentDemand.responsibleStaff.id,
          description: currentDemand.description,
          observation: currentDemand.observation,
          severity: currentDemand.severity,
          status: DEMAND_STATUS.ACTIVE,
          startedAt: parsedStartedAt
        }
      }
    )

    store.dispatch('resetCurrentDemand')

    if (store.rootState.pillars.personId === personId) {
      store.commit('setCurrentDemandDetails', data)
      store.dispatch('fetchCurrentDemandRecords', { personId, caseId })
    } else {
      addMixedDataLog('validateDemand')
    }
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/validateDemand' },
      { root: true }
    )
    // Throws error so the caller can notify users of a failed operation
    throw new Error(error)
  }
}

const runActionRecommendation = async (
  store,
  { caseId, personId, severity, referencedLinks }
) => {
  try {
    const currentDemandDetails = store.state.currentDemandDetails
    if (!currentDemandDetails || currentDemandDetails.caseId !== caseId) {
      return
    }

    // TODO: make it simpler
    const splittedCurrentDemandStart = currentDemandDetails.startedAt.split(' ')
    const date = splittedCurrentDemandStart[0].split('/').reverse().join('-')
    const hour = splittedCurrentDemandStart[1]
    const parsedStartedAt = new Date(`${date} ${hour}`)
      .toISOString()
      .split('Z')[0]

    const data = await instance.put(
      `persons/${personId}/health_demand/${caseId}`,
      {
        json: {
          responsibleStaffId: currentDemandDetails.responsibleStaff.id,
          description: currentDemandDetails.description,
          observation: currentDemandDetails.observation,
          severity: severity,
          referencedLinks: referencedLinks,
          status: DEMAND_STATUS.ACTIVE,
          startedAt: parsedStartedAt
        }
      }
    )

    store.commit('setCurrentDemandDetails', data)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/runActionRecommendation' },
      { root: true }
    )
  }
}

const searchDemands = async (
  store,
  { query, checkRole, options, type = null }
) => {
  try {
    const { results } = await instance.get('health_condition_search', {
      ...options,
      params: {
        type,
        query,
        check_role: checkRole,
        person_id: store.rootState.pillars.personId
      }
    })

    return results
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/demands/searchDemands' },
      { root: true }
    )
    return []
  }
}

/**
 * Search for disease
 * @param {*} store
 * @param {*} param
 * @returns
 * [
 *      {
 *          description: string (description + code)
 *          id: string
 *          type: CIAP_2/CID_10
 *          value: string (code)
 *      }
 * ]
 */
const searchDisease = async (
  store,
  { query, checkRole, options, type = null }
) => {
  try {
    return await instance.get('diseases', {
      ...options,
      params: {
        type,
        query,
        check_role: checkRole,
        person_id: store.rootState.pillars.personId
      }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'demands/searchDisease' },
      { root: true }
    )
    return []
  }
}

const getCipesByHealthCondition = async (store, healthConditionId) => {
  try {
    return await instance.get(`health_condition/${healthConditionId}/related`, {
      params: {
        types: 'CIPE'
      }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'demands/getCipesByHealthCondition',
        message: 'Erro ao recuperar CIPEs'
      },
      { root: true }
    )
    return []
  }
}

const fetchDemandDetailsById = (_, { personId, healthDemandId }) => {
  return instance.get(
    `persons/${personId}/health_demand/${healthDemandId}/person_case`
  )
}

const fetchDemandFollows = () => {
  return instance.get('health_demand/follows')
}

/**
 * Associate channel in demand
 * @param {Object} store
 * @param {Object} {channel: Object, caseId: String}
 * @returns undefined
 */
const setChannelDemand = async (store, { channel, caseId }) => {
  if (!validateChannel(channel)) {
    return false
  }
  try {
    const channelReturn = await channelsInstance.post(
      `channels/${store.rootState.pillars.personId}/associate_demand`,
      {
        json: { channel, caseId }
      }
    )

    store.commit('updateCurrentDemandDetails', {
      channelId: channelReturn.id
    })
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'channel/setChannelDemand',
        message: 'Não foi possível associar um canal à uma demanda'
      },
      { root: true }
    )
  }
}

const setFilterByChannel = (
  store,
  { filterTitle, filterName, channelId, disableTooltipText }
) => {
  store.commit('clearFiltersConfig')
  store.commit('setFiltersConfig', {
    filterTitle,
    filterName,
    disableTooltipText
  })
  store.commit('setFilterByChannel', channelId)
}

const clearFilters = store => {
  store.commit('clearFilters')
  store.commit('clearFiltersConfig')
  store.commit('clearPaginatedFilters')
}

const clearFiltersConfig = store => {
  store.commit('clearFiltersConfig')
}

const clearFilterByChannel = store => {
  store.commit('setFilterByChannel', null)
}

const validateChannel = channel => {
  return channel.name && channel.category
}

const resetCurrentDemand = store => {
  store.commit('setCurrentDemandDetails', null)
  store.commit('setCurrentDemandRecords', [])
  store.commit('setCurrentPagination', {
    range: null,
    stopLoad: false
  })
}

const setCurrentAction = (store, currentActionRecommendation) => {
  store.commit('setCurrentActionRecommendation', currentActionRecommendation)
}

const resetCurrentAction = store => {
  store.commit('setCurrentActionRecommendation', null)
}

const setCurrentDemandDetails = (store, { details }) => {
  store.commit('setCurrentDemandDetails', details)
}

const setCurrentDemandRecords = (store, { records }) => {
  store.commit('setCurrentDemandRecords', records)
}

const setPaginatedFilters = (store, filters) => {
  store.commit('setPaginatedFilters', filters)
}

export default {
  fetchDemands,
  fetchDemandsWithBaseRiskRating,
  fetchDemandsPaginated,
  fetchActionRecommendations,
  validateDemand,
  runActionRecommendation,
  searchDemands,
  addNewDemand,
  updateDemand,
  deleteDemand,
  fetchDemandDetailsById,
  fetchDemandFollows,
  setFilterByChannel,
  clearFilters,
  setChannelDemand,
  fetchCurrentDemandDetails,
  fetchCurrentDemandRecords,
  resetCurrentDemand,
  resetCurrentAction,
  setCurrentAction,
  searchDisease,
  setCurrentDemandDetails,
  setCurrentDemandRecords,
  getCipesByHealthCondition,
  setPaginatedFilters,
  clearFiltersConfig,
  clearFilterByChannel
}
